import React, { useState, useEffect } from "react";
import { 
  Typography, 
  Grid, 
  Card, 
  Avatar, 
  Button, 
  Box, 
  Container, 
  Chip,
  Divider,
  Paper,
  IconButton
} from "@mui/material";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { colors, modernColors } from "../components/Theme/WebTheme";
import moment from "moment/min/moment-with-locales";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import MoneyIcon from "@mui/icons-material/Money";
import PersonIcon from "@mui/icons-material/Person";
import LocalTaxiIcon from "@mui/icons-material/LocalTaxi";
import { makeStyles } from "@mui/styles";
import { MAIN_COLOR, SECONDORY_COLOR, FONT_FAMILY } from "../common/sharedFunctions"

const useStyles = makeStyles((theme) => ({
  root: {
    padding: '24px',
    fontFamily: FONT_FAMILY,
  },
  card: {
    borderRadius: "16px",
    backgroundColor: colors.WHITE,
    minHeight: 100,
    height: '100%',
    marginBottom: 20,
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
    overflow: 'hidden',
    transition: 'all 0.3s ease',
    '&:hover': {
      boxShadow: '0 8px 30px rgba(0, 0, 0, 0.1)',
      transform: 'translateY(-5px)'
    }
  },
  cardHeader: {
    padding: '16px 24px',
    fontWeight: 600,
    backgroundColor: modernColors.headerBg,
    color: modernColors.textPrimary,
    borderBottom: `1px solid ${modernColors.border}`,
    display: "flex",
    alignItems: "center",
    gap: "10px"
  },
  cardContent: {
    padding: '24px',
  },
  pageTitle: {
    fontWeight: 700,
    color: modernColors.textPrimary,
    marginBottom: '24px',
    fontSize: '1.75rem',
  },
  backButton: {
    fontWeight: 500,
    marginBottom: '16px',
    color: modernColors.primary,
    textTransform: 'none',
    '&:hover': {
      backgroundColor: `${modernColors.primary}15`,
    }
  },
  statusChip: {
    fontWeight: 600,
    color: colors.WHITE,
    padding: '8px 16px',
    height: 'auto',
    borderRadius: '12px',
  },
  label: {
    fontSize: '14px',
    color: modernColors.textSecondary,
    marginBottom: '4px',
  },
  value: {
    fontSize: '16px',
    color: modernColors.textPrimary,
    fontWeight: 500,
    wordBreak: 'break-word',
    paddingBottom: '8px',
  },
  infoRow: {
    marginBottom: '16px',
  },
  addressBox: {
    padding: '16px',
    borderRadius: '12px',
    backgroundColor: `${modernColors.primary}05`,
    marginBottom: '16px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
  },
  pickupAddress: {
    borderLeft: `4px solid ${modernColors.success}`,
  },
  dropAddress: {
    borderLeft: `4px solid ${modernColors.error}`,
  },
  waypointAddress: {
    borderLeft: `4px solid ${modernColors.warning}`,
  },
  avatar: {
    width: 120,
    height: 120,
    objectFit: "cover",
    marginBottom: '16px',
    boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
    border: `3px solid ${modernColors.primary}`,
  },
  profileSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: '24px',
  },
  divider: {
    margin: '16px 0',
  },
  statusLabel: {
    display: 'inline-block',
    marginLeft: '8px',
  }
}));

function BookingDetails() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const bookinglistdata = useSelector((state) => state.bookinglistdata);
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const settings = useSelector((state) => state.settingsdata.settings);
  const classes = useStyles();
  const role = useSelector(state => state.auth.profile.usertype);

  useEffect(() => {
    if (bookinglistdata.bookings) {
      const booking = bookinglistdata.bookings.filter(
        (item) => item.id === id.toString()
      )[0];

      if (booking) {
        setData(booking);
      } else {
        navigate("/404");
        setData([]);
      }
    }
  }, [bookinglistdata.bookings, id, navigate]);

  const getStatusColor = (status) => {
    switch(status) {
      case "CANCELLED":
        return modernColors.error;
      case "COMPLETE":
        return modernColors.success;
      case "NEW":
        return modernColors.primary;
      case "ACCEPTED":
        return modernColors.info;
      default:
        return modernColors.warning;
    }
  };

  const renderInfoRow = (label, value, icon = null) => {
    if (!value) return null;
    
    return (
      <div className={classes.infoRow}>
        <Typography className={classes.label}>
          {icon && icon} {label}
        </Typography>
        <Typography className={classes.value}>
          {value}
        </Typography>
      </div>
    );
  };

  const renderAddress = (type, address, addressDetails = null) => {
    if (!address) return null;
    
    const getAddressColor = () => {
      if (type === 'pickup') return modernColors.success;
      if (type === 'drop') return modernColors.error;
      return modernColors.warning; // waypoint
    };
    
    const getAddressTitle = () => {
      if (type === 'pickup') return t("pickup_address");
      if (type === 'drop') return t("drop_address");
      return t("waypoint_address");
    };
    
    const getAddressClassName = () => {
      if (type === 'pickup') return classes.pickupAddress;
      if (type === 'drop') return classes.dropAddress;
      return classes.waypointAddress;
    };
    
    return (
      <Box className={`${classes.addressBox} ${getAddressClassName()}`}>
        <Typography className={classes.label}>
          <LocationOnIcon fontSize="small" style={{ marginRight: '8px', color: getAddressColor() }} />
          {getAddressTitle()}
        </Typography>
        <Typography className={classes.value}>
          {address}
        </Typography>
        
        {/* Adres detayları */}
        {addressDetails && (
          <Box style={{ marginTop: '12px', paddingTop: '12px', borderTop: `1px solid ${modernColors.border}` }}>
            {addressDetails.apartmentNo && (
              <Typography variant="body2" style={{ color: modernColors.textSecondary, marginBottom: '4px' }}>
                <strong>{t("apartment_no")}:</strong> {addressDetails.apartmentNo}
              </Typography>
            )}
            {addressDetails.floor && (
              <Typography variant="body2" style={{ color: modernColors.textSecondary, marginBottom: '4px' }}>
                <strong>{t("floor")}:</strong> {addressDetails.floor}
              </Typography>
            )}
            {addressDetails.apt && (
              <Typography variant="body2" style={{ color: modernColors.textSecondary, marginBottom: '4px' }}>
                <strong>{t("apt")}:</strong> {addressDetails.apt}
              </Typography>
            )}
            {addressDetails.addressDescription && (
              <Typography variant="body2" style={{ color: modernColors.textSecondary, marginBottom: '4px' }}>
                <strong>{t("address_description")}:</strong> {addressDetails.addressDescription}
              </Typography>
            )}
            {addressDetails.contactPerson && (
              <Typography variant="body2" style={{ color: modernColors.textSecondary, marginBottom: '4px' }}>
                <strong>{t("contact_person")}:</strong> {addressDetails.contactPerson}
              </Typography>
            )}
            {role !== 'driver' && addressDetails.phoneNumber && (
              <Typography variant="body2" style={{ color: modernColors.textSecondary, marginBottom: '4px' }}>
                <strong>{t("phone_number")}:</strong> {addressDetails.phoneNumber}
              </Typography>
            )}
            
            {/* Kuryeye Not - Admin Görmeyecek (Sadece Sürücü/Müşteri) */}
            {role !== 'admin' && addressDetails.courierNote && (
              <Typography variant="body2" style={{ 
                color: modernColors.textSecondary, 
                marginBottom: '4px',
                marginTop: '8px',
                padding: '12px',
                backgroundColor: `${modernColors.warning}10`,
                borderRadius: '8px',
                border: `1px solid ${modernColors.warning}30`
              }}>
                <strong>{t("courier_note")}:</strong> {addressDetails.courierNote}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    );
  };

  const renderImage = (label, imageUrl, alt) => {
    if (!imageUrl) return null;
    
    return (
      <div className={classes.infoRow}>
        <Typography className={classes.label}>
          {label}
        </Typography>
        <Paper elevation={1} style={{ padding: '8px', display: 'inline-block', borderRadius: '8px' }}>
          <img
            src={imageUrl}
            alt={alt}
            style={{
              width: '100%',
              maxWidth: 220,
              height: 'auto',
              borderRadius: '8px',
            }}
          />
        </Paper>
      </div>
    );
  };

  return (
    <Container maxWidth="xl" className={classes.root} dir={isRTL === "rtl" ? "rtl" : "ltr"}>
      <Button
        variant="text"
        className={classes.backButton}
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate("/bookings")}
      >
        {t("go_back")}
      </Button>
      
      <Typography variant="h4" className={classes.pageTitle}>
        {t("booking_details")}
        {data?.status && (
          <Chip
            label={t(data.status)}
            style={{ backgroundColor: getStatusColor(data.status) }}
            className={classes.statusLabel}
          />
        )}
      </Typography>
      
      <Grid container spacing={3}>
        {/* Trip Information */}
        <Grid item xs={12} md={6} lg={6}>
          <Card className={classes.card}>
            <div className={classes.cardHeader}>
              <MoneyIcon />
              {t("ride_information")}
            </div>
            <div className={classes.cardContent}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  {renderInfoRow(t("booking_id"), data?.id)}
                  {renderInfoRow(t("booking_ref"), data?.reference)}
                  {renderInfoRow(t("trip_start_date"), data?.tripdate ? moment(data?.tripdate).format("lll") : "")}
                  {renderInfoRow(t("trip_start_time"), data?.trip_start_time)}
                  {renderInfoRow(t("trip_end_time"), data?.trip_end_time)}
                  {renderInfoRow(t("total_time"), data?.total_trip_time)}
                  {renderInfoRow(t("distance_web"), data?.distance ? (settings.convert_to_mile ? data?.distance + t("mile") : data?.distance + " " + t("km")) : "")}
                </Grid>
                <Grid item xs={12} md={6}>
                  {renderInfoRow(t("parcel_type_web"), data?.parcelTypeSelected ? data.parcelTypeSelected.description + " (" + data.parcelTypeSelected.amount + ")" : "")}
                  {renderInfoRow(t("parcel_option"), data?.optionSelected ? data.optionSelected.description + " (" + data.optionSelected.amount + ")" : "")}
                  {renderInfoRow(t("deliveryPerson"), data?.deliveryPerson)}
                  {renderInfoRow(t("deliveryPersonPhone"), data?.deliveryPersonPhone)}
                  {renderInfoRow(t("otp"), data?.otp)}
                  {renderInfoRow(t("cancellation_reason"), data?.reason)}
                </Grid>
                <Grid item xs={12}>
                  <Divider className={classes.divider} />
                </Grid>
                <Grid item xs={12}>
                  {renderAddress('pickup', data?.pickupAddress, data?.pickupAddressDetails)}
                  
                  {/* Ara Duraklar (Waypoints) */}
                  {data?.waypoints && data.waypoints.length > 0 && data?.waypointAddressDetails && (
                    <>
                      {data.waypoints.map((waypoint, index) => (
                        <div key={`waypoint-${index}`} style={{ marginTop: '12px' }}>
                          {renderAddress('waypoint', waypoint.add, data.waypointAddressDetails[index])}
                        </div>
                      ))}
                    </>
                  )}
                  
                  {renderAddress('drop', data?.dropAddress, data?.dropAddressDetails)}
                </Grid>
                <Grid item xs={12}>
                  <Divider className={classes.divider} />
                  <Typography variant="h6" style={{ 
                    color: modernColors.textPrimary, 
                    fontWeight: 'bold', 
                    marginTop: '16px', 
                    marginBottom: '12px',
                    fontSize: '16px'
                  }}>
                    {t("additional_info")}
                  </Typography>
                  
                  {renderInfoRow(t("tripInstructions"), data?.tripInstructions)}
                  {renderInfoRow(t("pickUpInstructions_web"), data?.pickUpInstructions)}
                  {renderInfoRow(t("deliveryInstructions"), data?.deliveryInstructions)}
                  
                  {data?.otherPerson && (
                    <div className={classes.infoRow}>
                      <Typography variant="subtitle1" style={{ color: modernColors.textPrimary, fontWeight: 'bold', marginTop: '16px', marginBottom: '8px' }}>
                        {t("for_other_person")}
                      </Typography>
                      {renderInfoRow(t("otherPerson"), data?.otherPerson)}
                      {renderInfoRow(t("otherPersonPhone"), data?.otherPersonPhone)}
                    </div>
                  )}
                  
                  {data?.feedback && (
                    <div style={{
                      marginTop: '16px',
                      padding: '16px',
                      backgroundColor: `${modernColors.info}10`,
                      borderRadius: '12px',
                      border: `1px solid ${modernColors.info}30`
                    }}>
                      <Typography variant="subtitle2" style={{ 
                        color: modernColors.info, 
                        fontWeight: 'bold',
                        marginBottom: '8px'
                      }}>
                        {t("feedback")}
                      </Typography>
                      <Typography variant="body2" style={{ color: modernColors.textSecondary }}>
                        {data.feedback}
                      </Typography>
                    </div>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  {renderImage(t("take_pickup_image_web"), data?.pickup_image, "pickup_image")}
                </Grid>
                <Grid item xs={12} md={6}>
                  {renderImage(t("take_deliver_image_web"), data?.deliver_image, "deliver_image")}
                </Grid>
              </Grid>
            </div>
          </Card>
        </Grid>
        
        {/* Payment Information */}
        <Grid item xs={12} md={6} lg={3}>
          <Card className={classes.card}>
            <div className={classes.cardHeader}>
              <MoneyIcon />
              {t("payment_info")}
            </div>
            <div className={classes.cardContent}>
              {renderInfoRow(t("payment_mode_web"), data?.payment_mode === 'wallet' ? t('wallet') : t(data?.payment_mode))}
              {renderInfoRow(t("payment_gateway"), data?.gateway)}
              <Divider className={classes.divider} />
              
              {renderInfoRow(t("trip_cost"), settings.swipe_symbol
                ? data?.trip_cost + " " + settings.symbol
                : settings.symbol + " " + data?.trip_cost)}
                
              {data?.customer_paid && renderInfoRow(t("Customer_paid"), settings.swipe_symbol
                ? data?.customer_paid + " " + settings.symbol
                : settings.symbol + " " + data?.customer_paid)}
                
              {renderInfoRow(t("discount_ammount"), settings.swipe_symbol
                ? data?.discount + " " + settings.symbol
                : settings.symbol + " " + data?.discount)}
                
              {role === 'admin' && data?.driver_share && renderInfoRow(t("driver_share"), settings.swipe_symbol
                ? data?.driver_share + " " + settings.symbol
                : settings.symbol + " " + data?.driver_share)}
                
              {role === 'admin' && data?.fleetCommission && parseFloat(data?.fleetCommission) > 0 && 
                renderInfoRow(t("fleet_admin_comission"), settings.swipe_symbol
                  ? data?.fleetCommission + " " + settings.symbol
                  : settings.symbol + " " + data?.fleetCommission)}
                  
              {role === 'admin' && renderInfoRow(t("convenience_fee"), settings.swipe_symbol
                ? data?.convenience_fees + " " + settings.symbol
                : settings.symbol + " " + data?.convenience_fees)}
                
              {renderInfoRow(t("cancellationFee"), settings.swipe_symbol
                ? data?.cancellationFee
                  ? data.cancellationFee
                  : (0).toFixed(settings.decimal) + " " + settings.symbol
                : settings.symbol + " " + (data?.cancellationFee
                  ? data.cancellationFee
                  : (0).toFixed(settings.decimal)))}
              
              <Divider className={classes.divider} />
              
              {data?.cashPaymentAmount && renderInfoRow(t("cash_payment_amount"), settings.swipe_symbol
                ? data?.cashPaymentAmount + " " + settings.symbol
                : settings.symbol + " " + data?.cashPaymentAmount)}
                
              {data?.cardPaymentAmount && renderInfoRow(t("card_payment_amount"), settings.swipe_symbol
                ? data?.cardPaymentAmount + " " + settings.symbol
                : settings.symbol + " " + data?.cardPaymentAmount)}
                
              {data?.usedWalletMoney && renderInfoRow(t("wallet_payment_amount"), settings.swipe_symbol
                ? data?.usedWalletMoney + " " + settings.symbol
                : settings.symbol + " " + data?.usedWalletMoney)}
              
              <Divider className={classes.divider} />
              
              {data?.payableAmount && renderInfoRow(t("payable_ammount"), settings.swipe_symbol
                ? data?.payableAmount + " " + settings.symbol
                : settings.symbol + " " + data?.payableAmount, 
                <MoneyIcon fontSize="small" style={{ color: modernColors.success }} />)}
            </div>
          </Card>
        </Grid>
        
        {/* Driver and Customer Information */}
        <Grid item xs={12} md={6} lg={3}>
          <Grid container spacing={3}>
            {/* Driver Info */}
            <Grid item xs={12}>
              <Card className={classes.card}>
                <div className={classes.cardHeader}>
                  <LocalTaxiIcon />
                  {t("driver_info")}
                </div>
                <div className={classes.cardContent}>
                  <div className={classes.profileSection}>
                    {data?.driver_image ? (
                      <Avatar
                        alt="driver profile image"
                        src={data?.driver_image}
                        className={classes.avatar}
                      />
                    ) : (
                      <Avatar className={classes.avatar}>
                        <AccountCircleIcon style={{ width: 80, height: 80 }} />
                      </Avatar>
                    )}
                    <Typography variant="h6" style={{ fontWeight: 600, marginBottom: '4px' }}>
                      {data?.driver_name || t("not_assigned_yet")}
                    </Typography>
                    {data?.driver && <Typography variant="body2" color="textSecondary">ID: {data.driver}</Typography>}
                  </div>
                  
                  {data?.driver_email && settings.AllowCriticalEditsAdmin && 
                    renderInfoRow(t("driver_email"), data.driver_email)}
                    
                  {data?.carType && renderInfoRow(t("car_type"), data.carType)}
                  {data?.vehicle_number && renderInfoRow(t("vehicle_no"), data.vehicle_number)}
                  {data?.fleetadmin && role === 'admin' && renderInfoRow(t("fleetadmins"), data.fleetadmin)}
                  {data?.driverDeviceId && renderInfoRow(t("device_id"), data.driverDeviceId)}
                </div>
              </Card>
            </Grid>
            
            {/* Customer Info */}
            <Grid item xs={12}>
              <Card className={classes.card}>
                <div className={classes.cardHeader}>
                  <PersonIcon />
                  {t("customer_info")}
                </div>
                <div className={classes.cardContent}>
                  <div className={classes.profileSection}>
                    {data?.customer_image ? (
                      <Avatar
                        alt="customer profile image"
                        src={data?.customer_image}
                        className={classes.avatar}
                      />
                    ) : (
                      <Avatar className={classes.avatar}>
                        <AccountCircleIcon style={{ width: 80, height: 80 }} />
                      </Avatar>
                    )}
                    <Typography variant="h6" style={{ fontWeight: 600, marginBottom: '4px' }}>
                      {data?.customer_name || ""}
                    </Typography>
                    {data?.customer && <Typography variant="body2" color="textSecondary">ID: {data.customer}</Typography>}
                  </div>
                  
                  {data?.customer_contact && settings.AllowCriticalEditsAdmin && 
                    renderInfoRow(t("customer_contact"), data.customer_contact)}
                    
                  {data?.customer_email && settings.AllowCriticalEditsAdmin && 
                    renderInfoRow(t("customer_email"), data.customer_email)}
                </div>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Container>
  );
}

export default BookingDetails;
